import { useState, useEffect, useCallback } from 'react';
import * as _ from 'lodash';
import { IoTimeOutline, IoChevronDown, IoChevronForward, IoSearchOutline, IoCloseCircleOutline } from 'react-icons/io5';
import { MdViewModule, MdViewList } from 'react-icons/md';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import Chip from '@mui/material/Chip';
import FileTypeFrame from '@CommonFunctionalDisplay/FileTypeFrame';
import FileRow from '@CommonFunctionalDisplay/FileRow';
import FileFilter from '@CommonFunctionalFilters/FileFilter';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import UserFileClobViewService from '@Helpers/URLHelpers/KzqFileService/UserFileClobViewService';
import ProjectService from '@Helpers/URLHelpers/ProjectService';
import ErrorHandler from '@Root/ErrorHandler';
import './RecentItems.scss';

export default function RecentItems(props) {
    const [cursor, setCursor] = useState('auto');
    const [loading, setLoading] = useState(false);
    const [searchActive, setSearchActive] = useState(false);
    const [userList, setUserList] = useState([]);
    const [thisWeekFiles, setThisWeekFiles] = useState([]);
    const [lastMonthFiles, setLastMonthFiles] = useState([]);
    const [lifeTimeFiles, setLifeTimeFiles] = useState([]);
    const [recentFileList, setRecentFileList] = useState([]);
    const [viewType, setViewType] = useState('grid');
    
    // Collapsible state variables
    const [expandedSections, setExpandedSections] = useState({
        search: true,
        thisWeek: true,
        lastMonth: false,
        lifeTime: false
    });

    const { currentProjectId } = useReduxProject();
    const { activeUserId } = useReduxActiveUser();
    const defaultFilters = {
        filter: {
            searchQuery: '',
            sortBy: 'createTimeStamp',
            sortAsc: false,
            projectIdList: [currentProjectId],
            fileStoreTypeList: [],
            tableTypeList: [],
            userIdList: [],
            createTimeStampTo: 0,
            createTimeStampFrom: 0
        },
        activeUserId: activeUserId
    };
    const [filters, setFilters] = useState(_.cloneDeep(defaultFilters));
    const userFileClobViewService = new UserFileClobViewService();
    const projectService = new ProjectService();
    const errorHandler = new ErrorHandler();
    const className = 'recentItems';

    useEffect(() => {
        renderData();
    }, []);

    const renderData = async () => {
        setLoading(true);
        renderUserList();
        try {
            let filterObj = defaultFilters;
            const threeMonthDate = new Date();
            threeMonthDate.setMonth(threeMonthDate.getMonth() - 3);
            filterObj.filter.createTimeStampFrom = threeMonthDate.getTime();
            let response = await userFileClobViewService.getFilteredList(filterObj);
            if (response.status < 400) {
                response = JSON.parse(response.body);
                categoriseFiles(response);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setLoading(false);
    };

    const renderUserList = async () => {
        try {
            if (userList.length === 0) {
                let userListRes = await projectService.getUserPermissionListForProject(currentProjectId);
                userListRes = JSON.parse(userListRes.body);
                setUserList(userListRes);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
    };

    const categoriseFiles = (fileList) => {
        const now = new Date();
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        let filterThisWeek = [];
        let filterStartMonth = [];
        let lifeTimeFiles = [];
        for (let i = 0; i < fileList.length; i++) {
            let item = fileList[i];
            let timeStamp = item.createTimeStamp;
            if (timeStamp >= startOfWeek.getTime()) {
                filterThisWeek.push(item);
            }
            if (!(timeStamp >= startOfWeek.getTime()) && timeStamp >= startOfMonth.getTime()) {
                filterStartMonth.push(item);
            }
            if (!(timeStamp >= startOfMonth.getTime())) {
                lifeTimeFiles.push(item);
            }
        }
        setThisWeekFiles(filterThisWeek);
        setLastMonthFiles(filterStartMonth);
        setLifeTimeFiles(lifeTimeFiles);
        setRecentFileList([]);
    };

    const onFilterChange = useCallback((filterValue) => {
        setFilters(filterValue);
    }, []);

    const clearSearch = useCallback(() => {
        setFilters(_.cloneDeep(defaultFilters));
        setSearchActive(false);
        renderData();
    }, []);

    const renderDataWithFilters = async (filter) => {
        setLoading(true);
        setSearchActive(true);
        try {
            let response = await userFileClobViewService.getFilteredList(filter);
            if (response.status < 400) {
                response = JSON.parse(response.body);
                setRecentFileList(response);
                setExpandedSections(prev => ({ ...prev, search: true }));
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setLoading(false);
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const getTotalCount = () => {
        if (searchActive) {
            return recentFileList.length;
        }
        return thisWeekFiles.length + lastMonthFiles.length + lifeTimeFiles.length;
    };

    const handleViewTypeChange = (type) => {
        setViewType(type);
    };

    return (
        <Box className={className} style={{ cursor: cursor }}>
            {/* Header */}
            <Box className={className + '__header'}>
                <Box className={className + '__header__left'}>
                    <IoTimeOutline className={className + '__header__icon'} />
                    <Box>
                        <Typography variant="h5" className={className + '__header__title'}>
                            Recent Files
                        </Typography>
                        <Typography variant="caption" className={className + '__header__subtitle'}>
                            {getTotalCount()} {getTotalCount() === 1 ? 'file' : 'files'} found
                        </Typography>
                    </Box>
                </Box>
                <Box className={className + '__header__right'}>
                    <Box className={className + '__viewToggle'}>
                        <Tooltip title="Grid View" arrow placement="bottom" slots={{ transition: Zoom }}>
                            <IconButton 
                                onClick={() => handleViewTypeChange('grid')}
                                className={`${className}__viewBtn ${viewType === 'grid' ? 'active' : ''}`}
                                size="small">
                                <MdViewModule />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="List View" arrow placement="bottom" slots={{ transition: Zoom }}>
                            <IconButton 
                                onClick={() => handleViewTypeChange('list')}
                                className={`${className}__viewBtn ${viewType === 'list' ? 'active' : ''}`}
                                size="small">
                                <MdViewList />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Box>

            {/* Filter Section */}
            <Box className={className + '__filter'}>
                <FileFilter
                    filters={filters}
                    onFilterChange={onFilterChange}
                    handleSearch={renderDataWithFilters}
                    clearFilters={clearSearch}
                    defaultFilters={defaultFilters}
                    userList={userList}
                    cursor={cursor}
                    setCursor={setCursor}
                />
            </Box>

            {/* Loading State */}
            {loading && (
                <Box className={className + '__loading'}>
                    <CircularProgress size={40} />
                    <Typography variant="body2">Loading recent files...</Typography>
                </Box>
            )}

            {/* Content */}
            {!loading && (
                <Box className={className + '__content'}>
                    {searchActive ? (
                        /* Search Results */
                        <Box className={className + '__section'}>
                            <Box 
                                className={className + '__section__header'}
                                onClick={() => toggleSection('search')}>
                                <Box className={className + '__section__header__left'}>
                                    {expandedSections.search ? (
                                        <IoChevronDown className={className + '__section__chevron'} />
                                    ) : (
                                        <IoChevronForward className={className + '__section__chevron'} />
                                    )}
                                    <IoSearchOutline className={className + '__section__icon'} />
                                    <Typography variant="subtitle1" className={className + '__section__title'}>
                                        Search Results
                                    </Typography>
                                    <Chip 
                                        label={recentFileList.length} 
                                        size="small" 
                                        className={className + '__section__count'}
                                    />
                                </Box>
                                <Tooltip title="Clear Search" arrow placement="left" slots={{ transition: Zoom }}>
                                    <IconButton 
                                        size="small" 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            clearSearch();
                                        }}
                                        className={className + '__section__action'}>
                                        <IoCloseCircleOutline />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Collapse in={expandedSections.search} timeout="auto">
                                {recentFileList.length === 0 ? (
                                    <Box className={className + '__empty'}>
                                        <IoSearchOutline className={className + '__empty__icon'} />
                                        <Typography variant="h6" className={className + '__empty__title'}>
                                            No files found
                                        </Typography>
                                        <Typography variant="body2" className={className + '__empty__subtitle'}>
                                            Try adjusting your search filters
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box className={viewType === 'grid' ? className + '__grid' : className + '__list'}>
                                        {recentFileList.map((item, index) => (
                                            viewType === 'grid' ? (
                                                <FileTypeFrame
                                                    key={index}
                                                    fileId={item.fileId}
                                                    fileData={item}
                                                    fileType={item?.fileType}
                                                    fileName={item?.fileName}
                                                    navigationParentId={item?.itemId}
                                                />
                                            ) : (
                                                <FileRow
                                                    key={index}
                                                    fileId={item.fileId}
                                                    fileData={item}
                                                    fileType={item?.fileType}
                                                    fileName={item?.fileName}
                                                    navigationParentId={item?.itemId}
                                                />
                                            )
                                        ))}
                                    </Box>
                                )}
                            </Collapse>
                        </Box>
                    ) : (
                        /* Time-based Categories */
                        <>
                            {/* This Week */}
                            {thisWeekFiles.length > 0 && (
                                <Box className={className + '__section'}>
                                    <Box 
                                        className={className + '__section__header'}
                                        onClick={() => toggleSection('thisWeek')}>
                                        <Box className={className + '__section__header__left'}>
                                            {expandedSections.thisWeek ? (
                                                <IoChevronDown className={className + '__section__chevron'} />
                                            ) : (
                                                <IoChevronForward className={className + '__section__chevron'} />
                                            )}
                                            <Typography variant="subtitle1" className={className + '__section__title'}>
                                                This Week
                                            </Typography>
                                            <Chip 
                                                label={thisWeekFiles.length} 
                                                size="small" 
                                                className={className + '__section__count'}
                                            />
                                        </Box>
                                    </Box>
                                    <Collapse in={expandedSections.thisWeek} timeout="auto">
                                        <Box className={viewType === 'grid' ? className + '__grid' : className + '__list'}>
                                            {thisWeekFiles.map((item, index) => (
                                                viewType === 'grid' ? (
                                                    <FileTypeFrame
                                                        key={index}
                                                        fileId={item.fileId}
                                                        fileData={item}
                                                        fileType={item?.fileType}
                                                        fileName={item?.fileName}
                                                        navigationParentId={item?.itemId}
                                                    />
                                                ) : (
                                                    <FileRow
                                                        key={index}
                                                        fileId={item.fileId}
                                                        fileData={item}
                                                        fileType={item?.fileType}
                                                        fileName={item?.fileName}
                                                        navigationParentId={item?.itemId}
                                                    />
                                                )
                                            ))}
                                        </Box>
                                    </Collapse>
                                </Box>
                            )}

                            {/* Last Month */}
                            {lastMonthFiles.length > 0 && (
                                <Box className={className + '__section'}>
                                    <Box 
                                        className={className + '__section__header'}
                                        onClick={() => toggleSection('lastMonth')}>
                                        <Box className={className + '__section__header__left'}>
                                            {expandedSections.lastMonth ? (
                                                <IoChevronDown className={className + '__section__chevron'} />
                                            ) : (
                                                <IoChevronForward className={className + '__section__chevron'} />
                                            )}
                                            <Typography variant="subtitle1" className={className + '__section__title'}>
                                                Last Month
                                            </Typography>
                                            <Chip 
                                                label={lastMonthFiles.length} 
                                                size="small" 
                                                className={className + '__section__count'}
                                            />
                                        </Box>
                                    </Box>
                                    <Collapse in={expandedSections.lastMonth} timeout="auto">
                                        <Box className={viewType === 'grid' ? className + '__grid' : className + '__list'}>
                                            {lastMonthFiles.map((item, index) => (
                                                viewType === 'grid' ? (
                                                    <FileTypeFrame
                                                        key={index}
                                                        fileId={item.fileId}
                                                        fileData={item}
                                                        fileType={item?.fileType}
                                                        fileName={item?.fileName}
                                                        navigationParentId={item?.itemId}
                                                    />
                                                ) : (
                                                    <FileRow
                                                        key={index}
                                                        fileId={item.fileId}
                                                        fileData={item}
                                                        fileType={item?.fileType}
                                                        fileName={item?.fileName}
                                                        navigationParentId={item?.itemId}
                                                    />
                                                )
                                            ))}
                                        </Box>
                                    </Collapse>
                                </Box>
                            )}

                            {/* Lifetime */}
                            {lifeTimeFiles.length > 0 && (
                                <Box className={className + '__section'}>
                                    <Box 
                                        className={className + '__section__header'}
                                        onClick={() => toggleSection('lifeTime')}>
                                        <Box className={className + '__section__header__left'}>
                                            {expandedSections.lifeTime ? (
                                                <IoChevronDown className={className + '__section__chevron'} />
                                            ) : (
                                                <IoChevronForward className={className + '__section__chevron'} />
                                            )}
                                            <Typography variant="subtitle1" className={className + '__section__title'}>
                                                Older Files
                                            </Typography>
                                            <Chip 
                                                label={lifeTimeFiles.length} 
                                                size="small" 
                                                className={className + '__section__count'}
                                            />
                                        </Box>
                                    </Box>
                                    <Collapse in={expandedSections.lifeTime} timeout="auto">
                                        <Box className={viewType === 'grid' ? className + '__grid' : className + '__list'}>
                                            {lifeTimeFiles.map((item, index) => (
                                                viewType === 'grid' ? (
                                                    <FileTypeFrame
                                                        key={index}
                                                        fileId={item.fileId}
                                                        fileData={item}
                                                        fileType={item?.fileType}
                                                        fileName={item?.fileName}
                                                        navigationParentId={item?.itemId}
                                                    />
                                                ) : (
                                                    <FileRow
                                                        key={index}
                                                        fileId={item.fileId}
                                                        fileData={item}
                                                        fileType={item?.fileType}
                                                        fileName={item?.fileName}
                                                        navigationParentId={item?.itemId}
                                                    />
                                                )
                                            ))}
                                        </Box>
                                    </Collapse>
                                </Box>
                            )}

                            {/* Empty State */}
                            {thisWeekFiles.length === 0 && lastMonthFiles.length === 0 && lifeTimeFiles.length === 0 && (
                                <Box className={className + '__empty'}>
                                    <IoTimeOutline className={className + '__empty__icon'} />
                                    <Typography variant="h6" className={className + '__empty__title'}>
                                        No recent files
                                    </Typography>
                                    <Typography variant="body2" className={className + '__empty__subtitle'}>
                                        Files you've accessed recently will appear here
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
}